// app/src/lib/receipt.ts — receipt config, thermal-style rendering, and printing.
import { api } from "./api";
import { toast } from "../modules/ui/Dialog";
import { setAppTimezone } from "./datetime";
import { BRAND_NAME } from "./brand";
import { code128Svg } from "./label";

/* The catalogue QR, drawn by the worker's own /api/public/qr endpoint — the
   same one the A4 documents already use, so there is one QR generator in the
   system rather than two that could disagree about what they encode. */
const CATALOGUE_QR = `/api/public/qr?d=${encodeURIComponent("https://uk.sloughscooters.com/")}&s=200`;

export interface ReceiptConfig {
  header: { name: string; legal_name?: string; address?: string; phone?: string; email?: string; vat_number?: string };
  currency: string; currency_minor: number;
  timezone?: string;
  paper: "80" | "58";
  footer: string;
  auto_print: boolean;
  show_vat: boolean;
  bridge?: boolean;     // when true, print via the on-site thermal print agent
  /** QR image printed at the foot of the receipt. Points at the public
   *  catalogue by default; the server can override it per organisation. */
  qr_url?: string;
}

export interface ReceiptLineData { description: string; qty?: number; amount_minor: number; notes?: string | null; }
export interface ReceiptPaymentLine {
  method: string;              // Cash, Card, Manual card…
  amount_minor: number;
  card_brand?: string;
  card_last4?: string;
  auth_code?: string;
  provider_transaction_id?: string;
  terminal_id?: string;
  merchant_reference?: string;
}
export interface ReceiptData {
  kind: "Sale" | "Rental" | "Bike sale" | "Service" | "Refund";
  ref: string;                 // receipt/agreement number
  when_ms?: number;
  cashier?: string;
  customer?: string;
  vehicle?: string;
  lines: ReceiptLineData[];
  subtotal_minor?: number;
  tax_minor?: number;
  total_minor: number;
  paid_minor?: number;
  change_minor?: number;
  change_method?: string | null;
  deposit_minor?: number;
  payments?: ReceiptPaymentLine[];   // tender breakdown (split payments, card metadata)
  note?: string;
  symbol?: string;             // currency symbol fallback
}

const SYMBOLS: Record<string, string> = { GBP: "£", USD: "$", EUR: "€", PKR: "₨", AED: "د.إ" };
let _cfg: ReceiptConfig | null = null;

/** Load (and cache) receipt config; falls back to a sensible default offline. */
export async function loadReceiptConfig(force = false): Promise<ReceiptConfig> {
  if (_cfg && !force) return _cfg;
  try {
    // The server config predates qr_url and will not carry it, so default here
    // rather than only on the error path — otherwise the QR would appear only
    // when the settings request FAILS, which is exactly backwards.
    const r = { qr_url: CATALOGUE_QR, ...await api.get<ReceiptConfig>("/settings/receipt") };
    if (!r.qr_url) r.qr_url = CATALOGUE_QR;
    _cfg = r;
    setAppTimezone(r.timezone);
    try { localStorage.setItem("epos.receipt.cfg", JSON.stringify(r)); } catch { /* ignore */ }
    return r;
  } catch {
    try { const cached = localStorage.getItem("epos.receipt.cfg"); if (cached) return (_cfg = { qr_url: CATALOGUE_QR, ...JSON.parse(cached) }); } catch { /* ignore */ }
    return { header: { name: "" }, currency: "GBP", currency_minor: 2, paper: "80", footer: "Thank you!", auto_print: false, show_vat: true, qr_url: CATALOGUE_QR };
  }
}
export function cachedReceiptConfig(): ReceiptConfig | null {
  if (_cfg) return _cfg;
  try { const c = localStorage.getItem("epos.receipt.cfg"); if (c) return (_cfg = JSON.parse(c)); } catch { /* ignore */ }
  return null;
}

const esc = (s: string) => String(s ?? "").replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));

function fmtMoney(minor: number, cfg: ReceiptConfig, symbolFallback?: string): string {
  const sym = symbolFallback || SYMBOLS[cfg.currency] || "";
  return sym + (minor / Math.pow(10, cfg.currency_minor)).toFixed(cfg.currency_minor);
}

/** Build a thermal-printer-friendly HTML receipt (80mm or 58mm). */
export function renderReceiptHtml(d: ReceiptData, cfg: ReceiptConfig): string {
  const widthMm = cfg.paper === "58" ? 58 : 80;
  const sym = d.symbol;
  const m = (v?: number) => (v == null ? "" : fmtMoney(v, cfg, sym));
  const when = (() => {
    const d2 = new Date(d.when_ms ?? Date.now());
    try { return new Intl.DateTimeFormat("en-GB", { timeZone: cfg.timezone, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(d2); }
    catch { return d2.toLocaleString("en-GB"); }
  })();
  const h = cfg.header;

  const lineRows = d.lines.map((l) => {
    const qty = l.qty && l.qty !== 1 ? `${l.qty} × ` : "";
    const main = `<tr><td class="desc">${esc(qty + l.description)}</td><td class="amt">${m(l.amount_minor)}</td></tr>`;
    const note = l.notes ? `<tr><td class="desc muted" style="font-style:italic;padding-left:12px">↳ ${esc(l.notes)}</td><td class="amt"></td></tr>` : "";
    return main + note;
  }).join("");

  const totalsRows: string[] = [];
  if (d.subtotal_minor != null) totalsRows.push(`<tr><td>Subtotal</td><td class="amt">${m(d.subtotal_minor)}</td></tr>`);
  if (cfg.show_vat && d.tax_minor != null) totalsRows.push(`<tr><td>VAT</td><td class="amt">${m(d.tax_minor)}</td></tr>`);
  if (d.deposit_minor) totalsRows.push(`<tr><td>Deposit</td><td class="amt">${m(d.deposit_minor)}</td></tr>`);
  totalsRows.push(`<tr class="grand"><td>TOTAL</td><td class="amt">${m(d.total_minor)}</td></tr>`);
  if (d.paid_minor != null) totalsRows.push(`<tr><td>Paid</td><td class="amt">${m(d.paid_minor)}</td></tr>`);
  if (d.change_minor) totalsRows.push(`<tr><td>Change${d.change_method ? ` (${d.change_method.replace("_", " ")})` : ""}</td><td class="amt">${m(d.change_minor)}</td></tr>`);

  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(d.ref)}</title>
<style>
  @page { size: ${widthMm}mm auto; margin: 0; }
  * { box-sizing: border-box; }
  body { width: ${widthMm}mm; margin: 0; padding: 6px 8px 14px; font-family: "Menlo","Consolas",monospace; color: #000; background: #fff; font-size: ${cfg.paper === "58" ? 11 : 12}px; line-height: 1.4; }
  .c { text-align: center; }
  .b { font-weight: 700; }
  .biz { font-size: ${cfg.paper === "58" ? 14 : 16}px; font-weight: 700; }
  .muted { color: #444; }
  hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { vertical-align: top; padding: 1px 0; }
  td.amt { text-align: right; white-space: nowrap; padding-left: 6px; }
  td.desc { word-break: break-word; }
  .grand td { font-weight: 700; font-size: ${cfg.paper === "58" ? 13 : 15}px; border-top: 1px solid #000; padding-top: 3px; }
  .meta { font-size: ${cfg.paper === "58" ? 10 : 11}px; }
  /* Thermal paper has no greys that survive: black on white, nothing else. */
  .bc { margin: 6px 0 2px; }
  .bc svg { width: 100%; height: 34px; }
  .ref { letter-spacing: .18em; }
  .qr { width: 96px; height: 96px; margin: 8px auto 2px; display: block; }
</style></head>
<body>
  <div class="c">
    <img src="${location.origin}/brand/logo.png" alt="${BRAND_NAME}" style="max-width:${cfg.paper === "58" ? 150 : 210}px;height:auto;margin:0 auto 10px;display:block" />
    ${h.address ? `<div class="meta">${esc(h.address)}</div>` : ""}
    ${h.phone ? `<div class="meta">${esc(h.phone)}</div>` : ""}
    ${h.vat_number ? `<div class="meta">VAT ${esc(h.vat_number)}</div>` : ""}
  </div>
  <hr>
  <div class="meta"><span class="b">${esc(d.kind)}</span> · ${esc(d.ref)}</div>
  <div class="meta muted">${esc(when)}</div>
  ${d.customer ? `<div class="meta muted">Customer: ${esc(d.customer)}</div>` : ""}
  ${d.vehicle ? `<div class="meta muted">Vehicle: ${esc(d.vehicle)}</div>` : ""}
  ${d.cashier ? `<div class="meta muted">Served by: ${esc(d.cashier)}</div>` : ""}
  <hr>
  <table>${lineRows || `<tr><td class="desc muted">No items</td><td class="amt"></td></tr>`}</table>
  <hr>
  <table>${totalsRows.join("")}</table>
  ${(d.payments && d.payments.length) ? `<hr><div class="meta b">Payment</div><table>${d.payments.map((p) => {
    const brand = [p.card_brand, p.card_last4 ? `****${p.card_last4}` : ""].filter(Boolean).join(" ");
    const label = [p.method, brand].filter(Boolean).join(" ");
    const sub = [p.auth_code ? `Auth ${p.auth_code}` : "", p.provider_transaction_id ? `Txn ${p.provider_transaction_id}` : "", p.terminal_id ? `Term ${p.terminal_id}` : ""].filter(Boolean).join(" · ");
    return `<tr><td class="desc">${esc(label)}${sub ? `<div class="meta muted">${esc(sub)}</div>` : ""}</td><td class="amt">${m(p.amount_minor)}</td></tr>`;
  }).join("")}</table>` : ""}
  ${d.note ? `<hr><div class="meta">${esc(d.note)}</div>` : ""}
  <hr>
  ${/* The reference as a SCANNABLE barcode, not only as printed digits.
        A customer comes back with a receipt and someone types BR1-001088 into
        the search box; scanning it is faster and cannot fat-finger a digit.
        Code 128-B because these references are letters, digits and a hyphen —
        EAN-13 could not encode them, and a barcode that scans as the wrong
        receipt is worse than none. Drawn locally, so a receipt still prints in
        full when the shop's internet is down. */""}
  <div class="c bc">${code128Svg(d.ref, 34)}</div>
  <div class="c meta ref">${esc(d.ref)}</div>
  ${/* QR to the public catalogue. It is the one thing a customer holding a
        receipt might actually want next — the parts they just bought, and the
        shop, on their phone. Fetched from the worker, so if the printer is
        offline the image simply does not render and the rest of the receipt is
        unaffected. */""}
  ${cfg.qr_url ? `<div class="c"><img class="qr" src="${esc(cfg.qr_url)}" alt=""><div class="meta muted">Scan for our parts catalogue</div></div>` : ""}
  <hr>
  <div class="c meta">${esc(cfg.footer || "")}</div>
</body></html>`;
}

/** Print a self-contained HTML document reliably across desktop, iOS Safari and
 *  Android/Samsung browsers.
 *
 *  The document is written into a dedicated full-bleed iframe (`.epos-print-frame`)
 *  and printed via its own contentWindow — which paginates the document correctly.
 *  Two mobile pitfalls are handled:
 *   • Some Android/Samsung browsers print the *parent* page when asked to print an
 *     off-screen iframe, dragging the whole app + any open modal onto the paper. So
 *     the frame is kept on-screen (the system print UI covers it anyway) and the
 *     global `@media print` rules hide the app shell whenever `html.epos-printing`
 *     is set — whichever document the engine actually prints, the output is just
 *     this one. (See global.css "Print".)
 *   • The frame is sized to its content so a parent-level print paginates the
 *     document across pages instead of clipping it to a single screen. */
export function printHtml(html: string) {
  // Drop any stale frame left over from a previous print.
  document.querySelectorAll("iframe.epos-print-frame").forEach((n) => n.remove());

  const iframe = document.createElement("iframe");
  iframe.className = "epos-print-frame";
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("tabindex", "-1");
  iframe.style.cssText =
    "position:fixed;left:0;top:0;width:100%;height:100%;border:0;background:#fff;z-index:2147483647;";
  document.body.appendChild(iframe);

  let cleaned = false;
  const onFocus = () => setTimeout(cleanup, 350);
  function cleanup() {
    if (cleaned) return; cleaned = true;
    document.documentElement.classList.remove("epos-printing");
    window.removeEventListener("focus", onFocus);
    try { iframe.remove(); } catch { /* ignore */ }
  }

  let started = false;
  const fire = () => {
    if (started) return; started = true;
    const win = iframe.contentWindow;
    if (!win) { cleanup(); return; }
    // Size to content so a parent-level print doesn't clip multi-page documents.
    try {
      const b = win.document.body, d = win.document.documentElement;
      const h = Math.max(b ? b.scrollHeight : 0, d ? d.scrollHeight : 0);
      if (h) iframe.style.height = h + "px";
    } catch { /* ignore */ }
    document.documentElement.classList.add("epos-printing");
    try { win.onafterprint = () => setTimeout(cleanup, 250); } catch { /* ignore */ }
    // Give layout one tick (large reports, web fonts, the logo image) before firing.
    setTimeout(() => {
      try { win.focus(); win.print(); }
      catch { try { window.print(); } catch { /* ignore */ } }
      // Attach AFTER printing so the win.focus() above can't trigger early cleanup.
      // Most engines refocus the opener when the print/save dialog closes.
      window.addEventListener("focus", onFocus);
      setTimeout(cleanup, 12000); // safety net if neither afterprint nor focus fires
    }, 160);
  };

  iframe.onload = fire;
  // Using srcdoc would be cleaner but isn't reliable on older Safari.
  const doc = iframe.contentWindow?.document;
  if (!doc) { cleanup(); return; }
  doc.open(); doc.write(html); doc.close();
  // A dynamically written same-origin iframe does not reliably re-fire `load`
  // after document.write() on every engine — so relying on onload alone can mean
  // the print never starts. Kick it off directly too; `started` keeps fire() idempotent.
  setTimeout(fire, 150);
}

/** Send a receipt to the on-site thermal print agent (via the server queue). */
export async function enqueuePrintJob(d: ReceiptData, copies = 1): Promise<{ id: string; status: string }> {
  return api.post<{ id: string; status: string }>("/print/jobs", {
    kind: d.kind, ref: d.ref, when_ms: d.when_ms, cashier: d.cashier, customer: d.customer,
    lines: d.lines, subtotal_minor: d.subtotal_minor, tax_minor: d.tax_minor, total_minor: d.total_minor,
    paid_minor: d.paid_minor, change_minor: d.change_minor, change_method: d.change_method,
    deposit_minor: d.deposit_minor, payments: d.payments, note: d.note, copies,
  });
}

// Briefly cache the agent probe so a burst of prints (several receipts in a row,
// or a preview + print) doesn't hit the network each time.
let _agentProbe: { live: boolean; at: number } | null = null;
const AGENT_PROBE_TTL_MS = 30_000;

/** True when an on-site thermal print agent is connected (heartbeat within 2 min).
 *  This is how we auto-detect a connected thermal printer. Cached briefly; pass
 *  force to bypass the cache (e.g. a Settings "refresh status"). */
export async function thermalAgentLive(force = false): Promise<boolean> {
  if (!force && _agentProbe && Date.now() - _agentProbe.at < AGENT_PROBE_TTL_MS) return _agentProbe.live;
  let live = false;
  try {
    const st = await api.get<{ agent: { last_seen_ms: number } | null; now_ms: number }>("/print/agent-status");
    live = !!st.agent && (st.now_ms - st.agent.last_seen_ms) < 120_000;
  } catch { live = false; }
  _agentProbe = { live, at: Date.now() };
  return live;
}

const _sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** After a receipt is queued for the thermal agent, watch the job and surface a
 *  success or error toast in the UI. Runs detached (not awaited) so the till is
 *  never blocked waiting on the printer. */
async function watchPrintJob(id: string) {
  const deadline = Date.now() + 25_000;
  while (Date.now() < deadline) {
    await _sleep(1200);
    let j: { status: string; error?: string | null } | null = null;
    try { j = await api.get<{ status: string; error?: string | null }>(`/print/jobs/${id}`); }
    catch { continue; } // transient poll error — keep watching
    if (!j) continue;
    if (j.status === "done") { toast("Receipt printed", "success"); return; }
    if (j.status === "failed") { toast(`Printer error: ${(j.error || "the receipt didn't print").slice(0, 140)}`, "error"); return; }
  }
  // Still queued/printing after the window — the agent is connected, so it will
  // print shortly. Tell the user it didn't fail rather than leaving them guessing.
  toast("Receipt sent — printing shortly", "info");
}

/** Render + print a receipt. Auto-detects the connected thermal printer: when the
 *  on-site print agent has a fresh heartbeat the receipt is sent to it as raw
 *  ESC/POS (correct paper width, no dialog) — from every receipt surface (till,
 *  workshop, transactions, payments) — and a success/error toast follows once the
 *  agent reports back. When no agent is connected, or queuing fails, or forceDialog
 *  is set (e.g. "Save as PDF"), it falls back to the device print dialog with the
 *  80/58mm HTML receipt. The bridge toggle is no longer required for routing — a
 *  connected agent is sufficient. */
export async function printReceipt(d: ReceiptData, opts?: { forceDialog?: boolean; copies?: number }) {
  const cfg = cachedReceiptConfig() ?? (await loadReceiptConfig());
  if (!opts?.forceDialog && (await thermalAgentLive())) {
    try {
      const job = await enqueuePrintJob(d, opts?.copies ?? 1);
      void watchPrintJob(job.id);     // background success/error toast
      return;
    } catch { /* agent unreachable mid-print — fall through to the device print dialog */ }
  }
  printHtml(renderReceiptHtml(d, cfg));
}
